import React, { useState, useRef, useEffect } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import AnnouncementBar from './AnnouncementBar';
import NotificationCard from './NotificationCard';
import NotificationPopup from './NotificationPopup';
import Button from '../Elements/Button/Button';
import Overlay from '../../hocs/Overlay/Overlay';
import ZohoForm from '../../hocs/ZohoForm/ZohoForm';
import CookiesNotification from '../../hocs/CookiesNotification/CookiesNotification';
import Navigation from './Navigation';
import { goToPage, changeDevice, cookieConsent, consumerType, changeStockistType, reshowCookieNotification } from '../../actions/environment';
import { clientInit, changeAddressInput } from '../../actions/checkout';
import { translate } from '../../utils/locale';
import { addEvent, removeEvent, hasClass, addClass, removeClass, offset, scrollTo, height } from '../../utils/dom';
import { filterActiveNotifications, splitCampaigns } from '../../utils/notification';
import { cookieConsentDisabled } from '../../config';
import s from './Header.scss';

let headerHeight = 102;
const breakPoints = [900, 600];
const media = { w_600: 'mobile', w_900: 'tablet', w_1200: 'desktop' };
// Length of the notificationStack__opening transition in Header.scss (width 0.2s, then bottom 0.3s).
const STACK_OPENING_DURATION = 300;

const Header = ({ properties, navigation, setup, header, footer, location, hasCookieDecision, ...props }) => {
	const [grid, setGrid] = useState(false);
	const [menuPopupHeight, setMenuPopupHeight] = useState('auto');
	const [menuOffset, setMenuOffset] = useState(null);
	// expanded -> collapsing -> collapsed -> opening -> expanded (see .notificationStack in Header.scss).
	const [stackPhase, setStackPhase] = useState('expanded');
	const headerElement = useRef();

	const collapseStack = () => setStackPhase((phase) => (phase === 'expanded' || phase === 'opening' ? 'collapsing' : phase));
	const toggleStack = () => setStackPhase((phase) => (phase === 'collapsing' || phase === 'collapsed' ? 'opening' : 'collapsing'));

	useEffect(() => {
		if (!hasCookieDecision) return undefined;

		addEvent(window, 'scroll.stackCollapse', () => {
			window.requestAnimationFrame(collapseStack);
		});

		return () => {
			removeEvent(window, 'scroll.stackCollapse');
		};
	}, [hasCookieDecision]);

	useEffect(() => {
		if (stackPhase === 'collapsing') {
			// Let the absolute (but not yet moved) layout paint first, so the move into the pile transitions.
			let frame = window.requestAnimationFrame(() => {
				frame = window.requestAnimationFrame(() => setStackPhase('collapsed'));
			});
			return () => window.cancelAnimationFrame(frame);
		}
		if (stackPhase === 'opening') {
			const timer = setTimeout(() => setStackPhase('expanded'), STACK_OPENING_DURATION);
			return () => clearTimeout(timer);
		}
		return undefined;
	}, [stackPhase]);

	useEffect(() => {
		if (typeof window !== 'undefined') {
			const $html = document.querySelector('html');
			let currentWidth = breakPoints.reduce((acc, bp) => (bp > window.innerWidth ? bp : acc), 1200);
			let device = media[`w_${currentWidth}`];
			if (device === 'tablet' || device === 'desktop') headerHeight = 102;
			else headerHeight = 72;

			props.clientInit();
			props.changeDevice(device);

			// Wait for page to be rendered with all relevant elements.
			const initiateSiteListeners = () => {
				if (!headerElement.current) {
					setTimeout(initiateSiteListeners, 200);
				}

				const startTop = window.pageYOffset || document.documentElement.scrollTop;
				const menuOffsetTemp = offset(headerElement.current).top + headerHeight;
				const menuOffsetWindowTop = startTop > offset(headerElement.current).top ? 0 : offset(headerElement.current).top;
				const bottomFunctions = window.innerWidth < 600 ? 166 : 0;

				let isScrolled = false;
				let headerIsFixed = false;
				let isScrollingUp = false;
				let lastScrolledDown = 0;
				let lastScrolledUp = 1e5;

				if (menuOffset === null) {
					setMenuOffset(menuOffsetTemp);
					setMenuPopupHeight(window.innerHeight - menuOffsetWindowTop - bottomFunctions);
				}

				const resizeEvent = () => {
					const updatedWidth = breakPoints.reduce((acc, bp) => (bp > window.innerWidth ? bp : acc), 1200);

					// Switch between mobile, table and desktop.
					if (currentWidth !== updatedWidth) {
						device = media[`w_${updatedWidth}`];
						props.changeDevice(device);
						currentWidth = updatedWidth;
						if (device === 'tablet' || device === 'desktop') headerHeight = 102;
						else headerHeight = 72;
					}
				};

				addEvent(window, 'resize.logoHeader', () => {
					window.requestAnimationFrame(resizeEvent);
				});

				const scrollEvent = () => {
					const top = window.pageYOffset || document.documentElement.scrollTop;
					if (menuOffset === null) {
						setMenuOffset(menuOffsetTemp);
					}
					const documentHeight = height(document);

					// make logo sticky when level with header.
					if (!headerIsFixed && top >= menuOffsetTemp) {
						headerIsFixed = true;
						addClass($html, 'u_headerIsFixed');
					} else if (headerIsFixed && top < menuOffsetTemp - headerHeight) {
						headerIsFixed = false;
						removeClass($html, 'u_headerIsFixed');
					}

					if (top > documentHeight - window.innerHeight - 102 && isScrolled) {
						isScrolled = false;
						addClass($html, 'u_pageIsScrolled');
					} else if (top > 0 && !isScrolled) {
						addClass($html, 'u_pageIsScrolled');
						isScrolled = true;
					} else if (top === 0) {
						removeClass($html, 'u_pageIsScrolled');
						removeClass($html, 'u_headerIsFixed');
						removeClass($html, 'u_pageIsScrollingUp');
						isScrolled = false;
						headerIsFixed = false;
					}

					if (top > lastScrolledDown) lastScrolledDown = top;
					if ((top < lastScrolledUp && isScrollingUp) || !isScrollingUp) lastScrolledUp = top;

					// Middle scrolling changing directions
					if (lastScrolledDown - top > 102 && !isScrollingUp) {
						addClass($html, 'u_pageIsScrollingUp');
						isScrollingUp = true;
					}
					if (top - lastScrolledUp > 102 && isScrollingUp) {
						removeClass($html, 'u_pageIsScrollingUp');
						lastScrolledDown = top;
						isScrollingUp = false;
					}
				};

				addEvent(window, 'scroll.logoHeader', () => {
					window.requestAnimationFrame(scrollEvent);
				});
			};

			initiateSiteListeners();

			if ('ontouchstart' in document.documentElement) {
				removeClass($html, 'no-touchevents');
				addClass($html, 'touchevents');
			}

			if (hasClass($html, 'no-touchevents')) {
				addEvent(window, 'touchstart.touchTest', () => {
					removeClass($html, 'no-touchevents');
					addClass($html, 'touchevents');
					removeEvent(window, 'touchstart.touchTest');
				});
			}

			addEvent(document.getElementById('root'), 'click.linkCapture', (e) => {
				const $link = e.target.closest('a');
				let linkLocation = ($link && $link.getAttribute('href')) || '';

				if ($link && hasClass($link, 'linkCapture')) {
					e.preventDefault();
					if (linkLocation === '#clear-consent') {
						if (!cookieConsentDisabled) props.reshowCookieNotification();
						return;
					}
					if (linkLocation === '#contractsales' || linkLocation === '#retailer') {
						props.changeStockistType(linkLocation === '#contractsales' ? 'contract-sales' : 'retailer');
						linkLocation = '#stockist-results';
					}
					if (linkLocation.indexOf('#') === 0) {
						const $id = document.getElementById(linkLocation.slice(1));
						if (linkLocation === '#stockist-results') {
							if ($id) {
								const scrollDistance = window.innerWidth < 600 ? 1639 : 1608;
								setTimeout(() => {
									scrollTo(400, Math.max(offset($id).top, scrollDistance));
								}, 400);
							}
						} else {
							scrollTo(400, offset($id).top);
						}
					} else {
						props.goToPage(linkLocation);
					}
				}
			});

			addEvent(window, 'keydown.general', (e) => {
				if (e.key === 'Escape') {
					if (grid) setGrid({ grid: false });
				} else if (e.shiftKey && e.ctrlKey && e.key === 'A') {
					// ctrl + shift + a = Toggle grid
					setGrid((prevState) => ({ grid: !prevState.grid }));
				}
			});
		}

		return () => {
			removeEvent(window, 'keydown.general');
			removeEvent(window, 'scroll.logoHeader');
			removeEvent(window, 'resize.logoHeader');
			removeEvent(window, 'message.acuityListener');
		};
	}, []);

	// const deliveryError = translate(setup, 'unableToSendToCountry').replace('%country%', countryName);
	if (properties.siteInitiated === false || properties.siteClosed === true) return null;

	const [showForm, toggleShowForm] = useState(false);
	const toggleFormHandler = () => {
		toggleShowForm((prevState) => !prevState);
	};

	const campaigns = splitCampaigns(header.campaign, setup);
	const popups = filterActiveNotifications(header.popup, setup);
	const consumers = setup.consumerTypeActive ? filterActiveNotifications(header.consumer, setup) : [];

	return (
		<>
			{ campaigns.scrolling.length > 0 && <AnnouncementBar items={campaigns.scrolling} setup={setup} /> }
			<header id="top" className={s.header} ref={headerElement}>
				<div className={s.header_fixedContainer}>
					<Navigation navigation={navigation} setup={setup} menuPopupHeight={menuPopupHeight} campaignMenu={header.campaignMenu} location={location} />
				</div>
			</header>
			{header.cookies && !cookieConsentDisabled && <CookiesNotification content={header.cookies} setup={setup} cookieConsent={props.cookieConsent} hasCookieDecision={hasCookieDecision} reshowNotification={props.reshowCookieBanner} />}
			{/* Earlier children sit lower in the stack, so popups are at the bottom, then Contact, then cards. */}
			<div className={[s.notificationStack, stackPhase !== 'expanded' ? s[`notificationStack__${stackPhase}`] : null].filter(Boolean).join(' ')}>
				{consumers.map((item) => <NotificationPopup key={item.name} variant="consumer" item={item} setup={setup} onConsumerChoice={props.consumerType} />)}
				{popups.map((item) => <NotificationPopup key={item.name} item={item} setup={setup} />)}
				<div className={s.contactForm}>
					<Button type="button" variantClasses={['button__card']} clickEventHandler={toggleFormHandler}>
						{translate(setup, footer?.zohoContactFormTitle)}
					</Button>
					<Overlay variantClasses={['overlay__zoho']} isShowing={showForm} closeOverlayHandler={toggleFormHandler}>
						<ZohoForm setup={setup} htmlBlob={footer?.zohoContactForm} callback={toggleFormHandler} />
					</Overlay>
				</div>
				{campaigns.cards.map((item) => <NotificationCard key={item.name} item={item} setup={setup} stackOpen={stackPhase === 'expanded'} />)}
			</div>
			<button type="button" className={[s.notificationToggle, stackPhase === 'collapsing' || stackPhase === 'collapsed' ? s.notificationToggle__collapsed : null].filter(Boolean).join(' ')} onClick={toggleStack} aria-label="Toggle notifications" aria-expanded={stackPhase === 'expanded' || stackPhase === 'opening'}>
				<span aria-hidden="true">+</span>
			</button>
			{ grid && <div className={s.grid}><div /></div> }
		</>
	);
};

const mapState = ({ environment, location, filter, productCache }) => {
	const { properties, navigation, setup, header, footer, pageType, countryName, reshowCookieNotification: reshowCookieBanner } = environment;
	return {
		properties,
		navigation,
		setup,
		header,
		footer,
		pageType,
		countryName,
		reshowCookieBanner,
		// Named apart from the `cookieConsent` action creator below (mapDispatchToProps) - giving
		// both the same key would let the action creator silently win over this state value.
		hasCookieDecision: Array.isArray(environment.cookieConsent),
		location,
		filter,
		productCache,
	};
};

export default hot(module)(connect(mapState, {
	cookieConsent,
	reshowCookieNotification,
	consumerType,
	changeAddressInput,
	goToPage,
	clientInit,
	changeDevice,
	changeStockistType,
})(Header));
